import { useEffect, useMemo, useState } from "react";
import { useLayerService } from "../../hooks";
import { useDataSelectionContext } from "./DataSelectionContext";
import { useBoundarySelectContext } from "../boundarySelection/BoundarySelectContext";
import { CensusOutputAreaType } from "../../types/Census/CensusOutputAreaType";
import * as yup from "yup";
import { Extent, intersects } from "ol/extent";
import { BoundarySelection } from "../../types/Boundary/BoundarySelection";
import DataSelectionModel from "./DataSelectionModal";
import { Col, Form, Row } from "react-bootstrap";
import LoadingSpinner from "../Loading/LoadingSpinner";
import { Field, Form as FormikForm } from "formik";
import { WfsGetFeatureRequest } from "../../types/Wfs/WfsGetFeatureRequest";

export interface CentroidFormData {
    outputType: number;
    releaseYear: number | undefined;
    outputArea: number | undefined;
}

const PopulationCentroids = () => {
    const layerName = "Basemap:PopulationWeightedCentroids";
    const layerService = useLayerService();
    const { layers, getLayer, wfsOrder } = useDataSelectionContext();
    const layer = useMemo(() => getLayer(layerName), [layers]);
    const { boundaries } = useBoundarySelectContext();
    const [releaseYears, setReleaseYears] = useState<number[]>([]);
    const [outputAreaTypes, setOutputAreaTypes] = useState<
        CensusOutputAreaType[]
    >([]);
    const [isLoading, setIsLoading] = useState({
        releaseYears: true,
        outputAreas: false,
    });

    const initialValues = useMemo(
        () =>
        ({
            outputType: 3,
            releaseYear: undefined,
            outputArea: undefined,
        } as CentroidFormData),
        [layer]
    );

    const schema = useMemo(
        () =>
            yup.object().shape({
                releaseYear: yup.number().required(),
                outputType: yup.number().required(),
                outputArea: yup.number().required(),
            }),
        [layer]
    );


  useEffect(() => {
    const fetchInitialData = async () => {
      let [years] = await Promise.all([layerService.GetCensusReleaseYears()]);
      const boundaryYears = getReleaseYearsFromBoundaries(boundaries);
      const yearToRemoved =[...years].filter(x => !boundaryYears.includes(x));
      setReleaseYears(yearToRemoved);
      setIsLoading({
        ...isLoading,
        releaseYears: false,
      });
    };

    fetchInitialData();
  }, [layerService]);

    const englandExtent = [-714522.7676150717, 6422893.150732635, 196334.81041443994, 7520905.547930526] as Extent;
    const scotlandExtent = [-962913.1311595398, 7291012.866813274, -80663.86298995547, 8593922.80525788] as Extent;

    useEffect(() => {
        boundaries.forEach((feature) => {
            setReleaseYears(getReleaseYearsFromBoundaries(boundaries));

        });
    }, [boundaries]);

    const getReleaseYearsFromBoundaries = (boundaries: BoundarySelection[]) => {
        const years = new Set();
        boundaries.forEach((feature) => {
            const geom = feature.geometry;
            const extent = geom.getExtent();
            const isInEngland = intersects(extent, englandExtent);
            const isInScotland = intersects(extent, scotlandExtent);
            let isEngland = false;
            let isScotland = false;
            if (isInEngland) {
                years.add(2022);
                isEngland = true;
            }
            if (isInScotland) {
                years.add(2021);
                isScotland = true;
            }
            if (isEngland && isScotland) {
                years.clear();
            }
        });
        return Array.from(years) as number[];
    };

    const onSubmit = (values: CentroidFormData) => {
        let filter = "ReleaseYear = " + values.releaseYear;
        filter += " AND OutputAreaType = " + values.outputArea;

        wfsOrder({
            service: "WFS",
            version: "2.0.0",
            request: "GetFeature",
            resultType: "Results",
            typeNames: layerName,
            outputFormat:
                values.outputType === 1 ? "GML" : values.outputType === 2 ? "excel" : values.outputType === 3 ? "csv" : "shapefile",
            filter: filter,
        } as WfsGetFeatureRequest);
    };

    const fetchOutputAreaTypes = async (releaseYear: number) => {
        setIsLoading({ ...isLoading, outputAreas: true });
        const response = await layerService.GetCensusOutputAreaTypes(releaseYear);
        setOutputAreaTypes(response);
        setIsLoading({ ...isLoading, outputAreas: false });
    };

    return (
        <>
            <DataSelectionModel
                headerText="Population Weighted Centroids"
                initialValues={initialValues}
                validationSchema={schema}
                size="lg"
                onSubmit={onSubmit}
                layer={layer}
            >
                {({ values, errors, setFieldValue }) => (
                    <FormikForm>
                        <Row className="justify-content-center">
                            <Col className="col-md-10">
                                <Form.Group>
                                    <Form.Label className="mb-0 mt-2">Release Year</Form.Label>
                                    <div>
                                        {!isLoading.releaseYears && (
                                            <Form.Control
                                                as="select"
                                                name="releaseYear"
                                                onChange={(e) => {
                                                    const selectedYear = parseInt(e.target.value);
                                                    setFieldValue("releaseYear", selectedYear);
                                                    setFieldValue("categoryId", -1);
                                                    setFieldValue("columnId", "");
                                                    setFieldValue("columns", []);
                                                    fetchOutputAreaTypes(selectedYear);
                                                }}
                                            >
                                                {values.releaseYear === undefined && (
                                                    <option value="">Select a release year</option>
                                                )}
                                                {releaseYears.map((year) => (
                                                    <option key={year} value={year}>
                                                        {year}
                                                    </option>
                                                ))}
                                            </Form.Control>
                                        )}
                                        {isLoading.releaseYears && <LoadingSpinner />}
                                    </div>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="justify-content-center">
                            <Col className="col-md-10">
                                <Form.Group>
                                    <Form.Label className="mb-0 mt-2">
                                        Geographical Area Type
                                    </Form.Label>
                                    <div>
                                        {!isLoading.outputAreas && (
                                            <Form.Control
                                                as="select"
                                                name="outputArea"
                                                onChange={(e) => {
                                                    setFieldValue("outputArea", parseInt(e.target.value));
                                                }}
                                                disabled={isLoading.releaseYears || !values.releaseYear}
                                            >
                                                {(isLoading.releaseYears || !values.releaseYear) && (
                                                    <option value="">Please select a release year</option>
                                                )}
                                                {(!isLoading.outputAreas) && (
                                                    <option value="">Select an output area</option>
                                                )}
                                                {outputAreaTypes.map((type) => (
                                                    <option
                                                        key={type.outputAreaTypeId}
                                                        value={type.outputAreaTypeId}
                                                    >
                                                        {type.name}
                                                    </option>
                                                ))}
                                            </Form.Control>
                                        )}
                                        {isLoading.outputAreas && <LoadingSpinner />}
                                    </div>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="justify-content-center">
                            <Col className="col-md-10">
                                <Form.Group>
                                    <Form.Label className="mb-0 mt-2">Output Type</Form.Label>
                                    <Field
                                        as={Form.Select}
                                        name="outputType"
                                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                            setFieldValue("outputType", parseInt(e.target.value));
                                        }}
                                    >
                                        {[
                                            { value: 1, label: "GML" },
                                            { value: 2, label: "Excel" },
                                            { value: 3, label: "CSV" },
                                            { value: 4, label: "ShapeFile" },
                                        ].map(({ value, label }) => (
                                            <option key={value} value={value}>
                                                {label}
                                            </option>
                                        ))}
                                    </Field>
                                </Form.Group>
                            </Col>
                        </Row>
                    </FormikForm>)}
            </DataSelectionModel></>
    );
}

export default PopulationCentroids;